import apiFetch from "@wordpress/api-fetch";
import {
	GlobalColorStyle,
	GlobalStyles,
	GlobalTypographyStyle,
} from "../types";
import { debounceAsync, deepClone, isEqual, localized, _set } from "../utils";
import { get, registerMiddleware, useSharedState } from "./useSharedState";

registerMiddleware("global-styles", (value, next) => {
	next(value);

	new GlobalStylesProcessor(value).process();
	if (isEqual(value, localized.configs["global-styles"])) return;
	const debouncedApiFetch = debounceAsync(apiFetch, 2500);
	debouncedApiFetch({
		path: "magazine-blocks/v1/settings",
		method: "POST",
		data: {
			"global-styles": JSON.stringify({
				...value,
				_modified: new Date().toISOString(),
			}),
		},
	}).catch(() => {});
});

// registerMiddleware("magazine-blocks-device", (value, next) => {
// 	next(value);
//
// 	console.log(value);
// 	setTimeout(() => {
// 		const _window = window["editor-canvas"];
// 		if (typeof _window !== "undefined") {
// 			new GlobalStylesProcessor(get("global-styles")).process();
// 		}
// 	}, 700);
// });

export const DEFAULT_IDS = ["primary", "secondary", "text", "accent"];

let counter = {
	colors: localized.configs["global-styles"].colors.filter(
		(color) => !DEFAULT_IDS.includes(color.id)
	).length,
	typographies: localized.configs["global-styles"].typographies.filter(
		(typography) => !DEFAULT_IDS.includes(typography.id)
	).length,
	categories_color:
		localized.configs["global-styles"].categories_color?.filter(
			(color) => !DEFAULT_IDS.includes(color.id)
		).length || 0,
};

type Style = GlobalColorStyle | GlobalTypographyStyle;

const useGlobalStyles = () => {
	const [styles, setStyles] = useSharedState<GlobalStyles>(
		"global-styles",
		localized.configs["global-styles"]
	);

	const generateIdName = (
		key: "typographies" | "colors" | "categories_color"
	) => {
		counter[key]++;
		const baseName =
			key === "typographies" ? "custom-typography" : "custom-color";
		let id = `${baseName}-${counter[key]}`;
		let name = baseName.replaceAll("-", " ");
		// @ts-ignore
		while ((styles?.[key] || []).find((n) => n.id === id)) {
			counter[key]++;
			id = `${baseName}-${counter[key]}` as string;
		}
		return {
			id,
			name: `${name}#${counter[key]}`,
		};
	};

	const updateStyleByKey = (
		key: "colors" | "typographies" | "categories_color",
		newStyle: Style
	) => {
		let nextStyles = deepClone(styles);
		let index = (nextStyles?.[key] || []).findIndex(
			(n: Style) => n.id === newStyle.id
		);
		if ("categories_color" === key) {
			if (-1 === index) {
				index = nextStyles?.categories_color?.length || 0;
			}
			nextStyles = _set(nextStyles, [key, index], newStyle);
		} else if (-1 !== index) {
			nextStyles = _set(nextStyles, [key, index], newStyle);
		} else {
			const idName = generateIdName(key);
			// @ts-ignore
			nextStyles = _set(
				nextStyles,
				[key, nextStyles?.[key]?.length ?? 0],
				{
					...newStyle,
					...idName,
				}
			);
		}
		setStyles(nextStyles as GlobalStyles);
		return nextStyles;
	};

	const removeStyleByKey = (key: "colors" | "typographies", id: string) => {
		const nextStyles = deepClone(styles);
		const removed = styles?.[key]?.findIndex((v) => v.id === id);
		if (typeof removed === "undefined" || -1 === removed) return styles;
		nextStyles?.[key].splice(removed, 1);
		setStyles(nextStyles as GlobalStyles);
		return nextStyles;
	};

	return {
		styles: styles as GlobalStyles,
		setStyles,
		updateStyleByKey,
		removeStyleByKey,
	} as const;
};

export class GlobalStylesProcessor {
	private _styles: GlobalStyles;
	private _colors: Array<string>;
	private _typographies: Array<string>;
	private _typographiesTablet: Array<string>;
	private _typographiesMobile: Array<string>;
	private _fonts: Record<string, Array<string | number>>;
	private _editorCanvas: Document;
	private _css: string;

	constructor(styles: GlobalStyles) {
		this._styles = styles;
		this._colors = [];
		this._typographies = [];
		this._typographiesTablet = [];
		this._typographiesMobile = [];
		this._fonts = {};
		this._css = "";
		this._editorCanvas = window["editor-canvas"]?.document;
	}

	process() {
		this._processColors();
		this._processCategoriesColor();
		this._processTypographies();
		this._makeStyle();
		if (this._editorCanvas) {
			this._makeStyle(this._editorCanvas);
		}
		// Monitor viewport changes for responsive styles
		this._monitorViewportChanges();
		// Ensure styles are injected into editor iframe
		this._ensureEditorStylesInjected();
	}

	private _processColors() {
		if (!this._styles.colors.length) return;
		for (const color of this._styles.colors) {
			if (!color.value) continue;
			this._colors.push(`--mzb-colors-${color.id}: ${color.value};`);
		}
	}

	private _processCategoriesColor() {
		if (!this._styles.categories_color?.length) return;
		for (const color of this._styles?.categories_color) {
			if (!color.value) continue;
			this._colors.push(
				`--mzb-categories-colors-${color.id}: ${color.value};`
			);
		}
	}

	private _processTypographies() {
		if (!this._styles.typographies.length) return;
		for (const typography of this._styles.typographies) {
			if (!typography.value) continue;
			let selectorCSS = "";
			const {
				family,
				weight = 400,
				size,
				lineHeight,
				transform,
				letterSpacing,
				fontStyle,
				decoration,
			} = typography.value;
			if (family && "Default" !== family) {
				if (!this._fonts[family]) {
					this._fonts[family] = [];
				}
				if (!this._fonts[family].includes(weight)) {
					this._fonts[family].push(weight);
				}
				this._typographies.push(
					`--mzb-font-families-${typography.id}: ${family};`
				);
				selectorCSS += `font-family: var(--mzb-font-families-${typography.id}) !important;`;
			}
			if (weight) {
				selectorCSS += `font-weight: var(--mzb-font-weights-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-weights-${typography.id}: ${weight};`
				);
			}
			if (transform && "default" !== transform) {
				selectorCSS += `text-transform: var(--mzb-font-transforms-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-transforms-${typography.id}: ${transform};`
				);
			}
			if (decoration && "default" !== decoration) {
				selectorCSS += `text-decoration: var(--mzb-font-decorations-${typography.id}) !important;`;
				this._typographies.push(
					`--mzb-font-decorations-${typography.id}: ${decoration};`
				);
			}
			if (fontStyle && "default" !== fontStyle) {
				this._typographies.push(
					`--mzb-font-styles-${typography.id}: ${fontStyle};`
				);
				selectorCSS += `font-style: var(--mzb-font-styles-${typography.id}) !important;`;
			}
			if (size) {
				this._processResponsiveTypography(
					size,
					`--mzb-font-sizes-${typography.id}`
				);
				selectorCSS += `font-size: var(--mzb-font-sizes-${typography.id}) !important;`;
			}
			if (letterSpacing) {
				this._processResponsiveTypography(
					letterSpacing,
					`--mzb-font-letter-spacings-${typography.id}`
				);
				selectorCSS += `letter-spacing: var(--mzb-font-letter-spacings-${typography.id}) !important;`;
			}
			if (lineHeight) {
				this._processResponsiveTypography(
					lineHeight,
					`--mzb-font-line-heights-${typography.id}`
				);
				selectorCSS += `line-height: var(--mzb-font-line-heights-${typography.id}) !important;`;
			}
			if (selectorCSS) {
				this._css =
					this._css +
					`.mzb-typography-${typography.id} {${selectorCSS}}`;
			}
		}
	}

	private _processResponsiveTypography(
		values: Record<string, { value: number; unit?: string }>,
		cssPropName: string
	) {
		if (values?.desktop?.value) {
			this._typographies.push(
				`${cssPropName}: ${values.desktop.value}${
					values.desktop?.unit ?? "px"
				};`
			);
		}
		if (values?.tablet?.value) {
			this._typographiesTablet.push(
				`${cssPropName}: ${values.tablet.value}${
					values.tablet?.unit ?? "px"
				};`
			);
		}
		if (values?.mobile?.value) {
			this._typographiesMobile.push(
				`${cssPropName}: ${values.mobile.value}${
					values.mobile?.unit ?? "px"
				};`
			);
		}
	}

	private _makeStyle(_doc = document) {
		const styleEl = _doc.querySelector(
			"#magazine-blocks-global-styles-css"
		);

		let css = `:root {${this._colors
			.concat(this._typographies)
			.join("\n")}}`;

		if (this._typographiesTablet && this._typographiesTablet.length > 0) {
			css += `\n@media (max-width: 1024px) { :root { ${this._typographiesTablet.join(
				"\n"
			)} } }`;
		}

		if (this._typographiesMobile && this._typographiesMobile.length > 0) {
			css += `\n@media (max-width: 768px) { :root { ${this._typographiesMobile.join(
				"\n"
			)} } }`;
		}
		if (this._css) {
			css += `\n${this._css}`;
		}

		if (this._fonts) {
			css =
				Object.entries(this._fonts)
					.map(([fontName, fontWeights]) => {
						const weightsString = fontWeights.join(",");
						return `@import url('https://fonts.googleapis.com/css2?family=${fontName.replace(
							/\s/g,
							"+"
						)}:wght@${weightsString}&display=swap');`;
					})
					.join("\n") +
				"\n" +
				css;
		}

		if (styleEl) {
			styleEl.innerHTML = css;
		} else {
			_doc.head.appendChild(
				Object.assign(_doc.createElement("style"), {
					id: "magazine-blocks-global-styles-css",
					innerHTML: css,
				})
			);
		}

		// Inject styles into iframe for tablet and mobile views
		this._injectStylesIntoIframe(css);
	}

	private _injectStylesIntoIframe(css: string) {
		// Always try to inject styles into iframe for all devices
		// This ensures compatibility across all viewports

		// Try to find the editor canvas iframe
		let iframe: HTMLIFrameElement | null = null;

		// Method 1: Check if window["editor-canvas"] exists
		if (window["editor-canvas"] && window["editor-canvas"].document) {
			this._injectStylesIntoDocument(
				window["editor-canvas"].document,
				css
			);
			return;
		}

		// Method 2: Look for iframe with id "editor-canvas"
		iframe = document.getElementById("editor-canvas") as HTMLIFrameElement;

		// Method 3: Look for common editor iframe selectors
		if (!iframe) {
			iframe = document.querySelector(
				'iframe[data-testid="editor-canvas"]'
			) as HTMLIFrameElement;
		}

		// Method 4: Look for any iframe that might be the editor
		if (!iframe) {
			const iframes = document.querySelectorAll("iframe");
			for (const frame of iframes) {
				if (
					frame.src &&
					(frame.src.includes("editor") ||
						frame.src.includes("canvas"))
				) {
					iframe = frame;
					break;
				}
			}
		}

		// Method 5: Look for any iframe in the document (fallback)
		if (!iframe) {
			const allIframes = document.querySelectorAll("iframe");
			if (allIframes.length > 0) {
				// Use the first iframe as a fallback
				iframe = allIframes[0];
			}
		}

		if (iframe && iframe.contentDocument) {
			this._injectStylesIntoDocument(iframe.contentDocument, css);
		} else {
			// Log for debugging
			console.log("Magazine Blocks: No iframe found for style injection");
		}
	}

	private _injectStylesIntoDocument(doc: Document, css: string) {
		try {
			let styleEl = doc.querySelector(
				"#magazine-blocks-global-styles-css"
			);

			if (styleEl) {
				styleEl.innerHTML = css;
			} else {
				doc.head.appendChild(
					Object.assign(doc.createElement("style"), {
						id: "magazine-blocks-global-styles-css",
						innerHTML: css,
					})
				);
			}
		} catch (error) {
			console.warn("Could not inject styles into document:", error);
		}
	}

	private _monitorViewportChanges() {
		if (typeof window !== "undefined") {
			let currentViewport = this._getCurrentViewport();

			const handleResize = () => {
				const newViewport = this._getCurrentViewport();

				// Only reapply styles if viewport changed
				if (newViewport !== currentViewport) {
					currentViewport = newViewport;
					this._makeStyle();
					if (this._editorCanvas) {
						this._makeStyle(this._editorCanvas);
					}
				}
			};

			// Debounce resize events
			let resizeTimeout: number;
			window.addEventListener("resize", () => {
				clearTimeout(resizeTimeout);
				resizeTimeout = window.setTimeout(handleResize, 100);
			});
		}
	}

	private _getCurrentViewport(): "desktop" | "tablet" | "mobile" {
		if (window.innerWidth <= 768) {
			return "mobile";
		} else if (window.innerWidth <= 1024) {
			return "tablet";
		}
		return "desktop";
	}

	private _ensureEditorStylesInjected() {
		// Wait for the editor to be ready
		if (typeof window !== "undefined") {
			// Try to inject styles immediately
			setTimeout(() => {
				this._injectStylesIntoEditorIframe();
			}, 100);

			// Also try after a longer delay to catch late-loading iframes
			setTimeout(() => {
				this._injectStylesIntoEditorIframe();
			}, 1000);

			// Listen for editor ready events
			window.addEventListener("load", () => {
				this._injectStylesIntoEditorIframe();
			});

			// Listen for WordPress editor events
			if (window.wp && window.wp.data) {
				window.wp.data.subscribe(() => {
					this._injectStylesIntoEditorIframe();
				});
			}
		}
	}

	private _injectStylesIntoEditorIframe() {
		// Get the current CSS
		const css = this._generateCSS();
		if (!css) return;

		// Try multiple methods to find the editor iframe
		let iframe: HTMLIFrameElement | null = null;

		// Method 1: WordPress editor iframe
		iframe = document.querySelector(
			".wp-block-editor__container iframe"
		) as HTMLIFrameElement;

		// Method 2: Gutenberg editor iframe
		if (!iframe) {
			iframe = document.querySelector(
				".block-editor iframe"
			) as HTMLIFrameElement;
		}

		// Method 3: Editor canvas iframe
		if (!iframe) {
			iframe = document.querySelector(
				"#editor-canvas"
			) as HTMLIFrameElement;
		}

		// Method 4: Any iframe with editor-related content
		if (!iframe) {
			const iframes = document.querySelectorAll("iframe");
			for (const frame of iframes) {
				if (
					frame.classList.contains("editor-canvas") ||
					frame.id === "editor-canvas" ||
					frame.getAttribute("data-testid") === "editor-canvas"
				) {
					iframe = frame;
					break;
				}
			}
		}

		// Method 5: Check window["editor-canvas"]
		if (
			!iframe &&
			window["editor-canvas"] &&
			window["editor-canvas"].document
		) {
			this._injectStylesIntoDocument(
				window["editor-canvas"].document,
				css
			);
			return;
		}

		if (iframe && iframe.contentDocument) {
			this._injectStylesIntoDocument(iframe.contentDocument, css);
		}
	}

	private _generateCSS(): string {
		let css = `:root {${this._colors
			.concat(this._typographies)
			.join("\n")}}`;

		if (this._typographiesTablet && this._typographiesTablet.length > 0) {
			css += `\n@media (max-width: 1024px) { :root { ${this._typographiesTablet.join(
				"\n"
			)} } }`;
		}

		if (this._typographiesMobile && this._typographiesMobile.length > 0) {
			css += `\n@media (max-width: 768px) { :root { ${this._typographiesMobile.join(
				"\n"
			)} } }`;
		}

		if (this._css) {
			css += `\n${this._css}`;
		}

		if (this._fonts) {
			css =
				Object.entries(this._fonts)
					.map(([fontName, fontWeights]) => {
						const weightsString = fontWeights.join(",");
						return `@import url('https://fonts.googleapis.com/css2?family=${fontName.replace(
							/\s/g,
							"+"
						)}:wght@${weightsString}&display=swap');`;
					})
					.join("\n") +
				"\n" +
				css;
		}

		return css;
	}
}

export default useGlobalStyles;
